import { Component, ElementRef, EventEmitter, Input, Output, OnDestroy, ViewChild, signal, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConnectionService } from '@services/connection-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { EventLoggerService } from '@services/event-logger-service';
import { FirebaseAndBranchEvent } from '@services/event-logger-service/event-logger.constant';
import { LocalStorageService } from '@services/local-storage-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { BroadcastService } from '@services/broadcast-service';
import { ChatProgressService } from '@services/chat-progress-service/chat-progress.service';
import { TimeService } from '@services/time-service';
import { CurrentComponentService } from '@services/current-component';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { ToolbarComponent } from '@components/toolbar/toolbar.component';
import { ToolbarTabsComponent } from '@components/toolbar-tabs/toolbar-tabs.component';
import { ApiClientConstant, ParseKeys, Table } from '@cureskin/api-client';
import { LanguageChangeComponent } from '@shared/bottom-sheet-layouts/language-change/language-change.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ConsultationSessionService } from '@services/consultation-session-service';
import { UnpaidPendingCallService } from '@services/pending-call-banner-service';
import { TimeSlot } from '@shared/bottom-sheet-layouts/pending-call-bottomsheet/request-call-bottomsheet/request-call-bottomsheet.component';
import { UnpaidUserDoctorCallVariant } from '@services/pending-call-banner-service/types';
import { AppConfig } from '../../../app.config';
import { RegimenExpiryBannerData, RegimenExpiryService } from '@services/regimen-expiry-service/regimen-expiry.service';
import { DoctorProfile } from '@cureskin/api-client-rn';
import { UserAllocatedDoctorService } from '@services/user-allocated-doctor-service/user-allocated-doctor.service';
import { RegimenService } from '@services/regimen-service';
import { RegimenExpiryBottomsheetComponent } from '@shared/bottom-sheet-layouts/regimen-expiry-bottomsheet/regimen-expiry-bottomsheet.component';
import { GetUserInstructionDetails } from '@cureskin/api-client/src/server-api';
import { ChatSkipLastQuestionsExperiment } from '@services/experiment/experiment.service.types';
import { InstructionsService } from '@services/instructions-service/instructions.service';
import { RegimenViewState, VIEW_TO_ROUTE_MAP } from './regimen.types';
import { REGIMEN_ROUTE_PATHS, REGIMEN_QUERY_PARAMS, REGIMEN_VIEW_STATES } from './regimen.constants';
import { AllocatedRegimen } from '../../../app-constant-types';

export interface TabData {
  count: number;
  types: string[];
  selectedTab: number;
}

@Component({
  selector: 'user-view-regimen',
  templateUrl: './user-view-regimen.html',
  styles: ':host { @apply tw-block tw-h-full; }',
  standalone: false,
})
export class UserViewRegimenComponent implements OnDestroy {
  private readonly REMINDER_EVENT_DELAY_MS: number = 600;
  private readonly REMINDER_CLOSE_DELAY_MS: number = 5000;

  autoScrollToBottom: () => void = () => {
    if (this.regimenModuleContainer) {
      this.regimenModuleContainer.nativeElement.scroll(0, this.regimenModuleContainer.nativeElement.scrollHeight);
      this.regimenModuleContainer.nativeElement.scrollTop = this.regimenModuleContainer.nativeElement.scrollHeight;
    }
  };
  @ViewChild('toolbar', { static: false }) toolbar: ToolbarComponent;
  @ViewChild('toolbarTabs', { static: false }) toolbarTabs: ToolbarTabsComponent;
  @ViewChild('regimenModuleContainer', { static: false }) regimenModuleContainer: ElementRef;
  @Input('animation') animation: any;
  isTabActive: boolean = false;
  isFromHowToUse: boolean = false;
  @Input('refresh')
  set refresh(isActive: boolean) {
    this.isTabActive = isActive;
    if (isActive) {
      this.currentComponentService.set(this);
    } else {
      this.regimen = {} as AllocatedRegimen;
      if (this.regimenModuleContainer) this.regimenModuleContainer.nativeElement.scrollTop = 0;
      this.currentComponentService.remove(this);
    }
  }
  @Output('showNewRegimen') showRegimen: EventEmitter<any> = new EventEmitter<any>();
  headerHeight: number = 0; // Toolbar + ToolbarTabs
  tabData: TabData = { count: 0, types: [], selectedTab: 0 };
  isInternalUser: boolean;
  username: string;
  supportTicketId: string;
  user: any;
  consultationSession: any;
  regimenClasses: string[];
  regimen: AllocatedRegimen = {} as AllocatedRegimen;
  regimens: Array<any> = [];
  userRegimens = signal<Array<AllocatedRegimen>>([]);
  experiments: any[];
  showDescriptionExperiment: boolean = false;
  socialProof: any[] = [];
  loading: boolean = false;
  tabSwitching: boolean = false;
  loadingByChild: boolean = false;
  subscriptions: Array<Subscription> = [];
  offerBanner: any = {};
  offerTimer: any = {};
  redirectAfterRegimenPreparation: boolean = false;
  isCartUpdated: boolean = false;
  cartState: any = {
    totalPrice: 0,
    discountedPrice: 0,
  };
  cartDiscount: number;
  plan: string = '';
  allocatedRegimens: Array<any> = [];
  isChatLoading: boolean = false;
  chatProgressValue: number;
  chatProgressType: string;
  isRegimenNewPropositionUI: boolean = false;
  newInstructionUIExperiment: boolean = false;
  dietBannerV2: boolean = false;
  dietBannerRevamp: boolean = false;
  public lastScrollTop: number = 0;
  scrollingDown: boolean = false;
  botReplyInProgress: boolean;
  latestBotChat: Table.ConsultationSessionChat;
  reminderToggle: boolean = false;
  reminderEventSent: boolean = false;
  showReminderContainer: boolean = true;
  isNotIOSDevice: boolean;
  isMorningNightTabsVisible: boolean = false;
  isKeyboardVisible = false;
  regimenRehaulV1Experiment: { variant: any; };
  nudgeDoctorCallOnBack: boolean = false;
  unpaidUserDoctorCallExpVariants: UnpaidUserDoctorCallVariant;
  unpaidUserDoctorCalls: any[];
  regimenRehaulV2: boolean = false;
  instructionsLanguageChangeExp: boolean = false;
  showRegimenExpiryBanner: boolean = false
  regimenExpiryBannerData: RegimenExpiryBannerData;
  userFirstName: string;
  allocatedDoctor: DoctorProfile;
  userDrImage: string;
  regimenExpiryExperiment: any;
  defaultDoctorImage: string = 'https://cdn.cureskin.com/app/img/dr-charu-main-concern.png';
  shouldShowAddOnInstructionTab = signal<boolean>(false);
  userInstructions: GetUserInstructionDetails;
  addOnInstructions = signal<GetUserInstructionDetails['userInstructions']['ADDITIONAL_PRODUCTS']>([]);
  hasBodyRegimen: boolean = false;
  shouldShowAddOnInstructionUI = signal<boolean>(false);
  chatSkipLastQuestionsExperiment: ChatSkipLastQuestionsExperiment;
  private hasSentOthersTabImpression: boolean = false;
  constructor(private conn: ConnectionService,
    private router: Router,
    private route: ActivatedRoute,
    public appConfig: AppConfig,
    private eventLoggerService: EventLoggerService,
    private localStorageService: LocalStorageService,
    private broadcast: BroadcastService,
    private timeService: TimeService,
    private currentComponentService: CurrentComponentService,
    private appBridge: AppWebBridgeService,
    private commonUtil: CommonUtilityHelper,
    private readonly appWebBridge: AppWebBridgeService,
    private readonly bottomSheet: MatBottomSheet,
    public chatProgressService: ChatProgressService,
    private readonly consultationSessionService: ConsultationSessionService,
    private readonly pendingCallService: UnpaidPendingCallService,
    private readonly experimentService: ExperimentService,
    private readonly regimenExpiryService: RegimenExpiryService,
    private readonly instructionsService: InstructionsService,
    private readonly regimenService: RegimenService,
    private readonly userAllocatedDoctorService: UserAllocatedDoctorService,
  ) {
    this.currentComponentService.set(this);
    chatProgressService.getSelectedChatType().subscribe((value: string): void => {
      this.chatProgressType = value;
    });
    chatProgressService.getChatProgressState().subscribe((value: number): void => {
      this.chatProgressValue = value;
    });

    effect(() => {
      const instructions = this.addOnInstructions();
      if (instructions !== undefined) {
        this.updateAddOnInstructionsFlags();
      }
    });
    effect(() => {
      this.userRegimens();
      this.updateAddOnInstructionsFlags();
    });
  }

  async ngOnInit(): Promise<any> {
    this.updateStatusBarColor();
    this.isFromHowToUse = this.route.snapshot.queryParams?.fromHowToUse;
    if (this.route.snapshot.queryParams.supportTicketId) {
      this.supportTicketId = this.route.snapshot.queryParams.supportTicketId;
    }
    this.regimenClasses = [
      this.appConfig.Shared.Regimen.Class.FACE,
      this.appConfig.Shared.Regimen.Class.HAIR,
      this.appConfig.Shared.Regimen.Class.BODY,
    ];
    this.user = await this.conn.getActingUser();
    this.userFirstName = this.user?.get('PatientName')?.split(' ')[0];
    this.isNotIOSDevice = (await this.appBridge?.requestOSInformation()) !== 'iOS';
    this.showReminderContainer = !this.user.get('hasEnabledReminderForInstructions');
    if (!this.user?.get('allocatedDoctor')) this.conn.getActingUser().fetch();
    this.isInternalUser = this.conn.isInternalUser();
    this.regimens = this.regimenClasses.map((regimenClass: string): Object => ({ class: regimenClass }));
    await this.setUserExperiments();
    await this.fetchAndUpdateAddOnInstructionsFlags();
    this.activateSubscription();
    this.subscriptions.push(
      this.pendingCallService.callCreated$.subscribe(async () => {
        await this.fetchUnpaidUserDoctorCalls();
      })
    );
    this.broadcast.on<boolean>('KEYBOARD_VISIBLE').subscribe(isVisible => {
      this.isKeyboardVisible = isVisible;
    });
    await this.shouldShowRegimenExpiryBanner();
    this.setTabData(this.hasBodyRegimen, this.addOnInstructions()?.length > 0);
    this.updateStatusBarColor();
  }

  updateAddOnInstructionsFlags(): void {
    this.shouldShowAddOnInstructionTab.set(this.addOnInstructions()?.length > 0);
    if (this.route.snapshot.queryParams.class === 'OTHERS' && this.addOnInstructions()?.length > 0) {
      this.shouldShowAddOnInstructionUI.set(true);
    } else {
      this.shouldShowAddOnInstructionUI.set(false);
    }
  }

  async fetchAndUpdateAddOnInstructionsFlags(): Promise<void> {
    if (this.user.get('orderState') !== this.appConfig.Shared.User.OrderState.DELIVERED) {
      this.shouldShowAddOnInstructionUI.set(false);
      return;
    };
    this.userInstructions = await this.conn.getInstructionsForUser();
    this.instructionsService.setUserInstructions(this.userInstructions);
    this.addOnInstructions.set(this.userInstructions.userInstructions.ADDITIONAL_PRODUCTS);
    this.updateAddOnInstructionsFlags();
  }

  processRegimenRehaulV1Experiment(experiment: { variant: any; }): void {
    if (!experiment) return;

    const regimenClass = ((this.route.snapshot.queryParams?.class as string) ?? this.appConfig.Shared.Regimen.Class.FACE).toLowerCase();
    const userLanguage = ((this.user?.get('languagePreference') as string) ?? this.appConfig.Shared.Languages.EN).toLowerCase();

    const result = this.experimentService.processRegimenRehaulV1Experiment(experiment, { regimenClass, userLanguage });

    this.regimenRehaulV1Experiment = experiment;
    this.nudgeDoctorCallOnBack = result.nudgeDoctorCallOnBack ?? false;
  }

  async setUserExperiments(): Promise<void> {
    this.experiments = await this.conn.findUserActiveExperiments(this.route.snapshot.queryParams.username);
    this.experiments.forEach((exp: any): void => {
      if (exp.key === 'instruction_page_redesign') {
        this.newInstructionUIExperiment = true;
      }
      if (exp.key === 'unpaid_user_pending_call_banner') {
        this.unpaidUserDoctorCallExpVariants = exp.variant;
      }
      if (exp.key === 'regimen_rehaul_v1') {
        this.processRegimenRehaulV1Experiment(exp);
      }
      if (exp.key === 'regimen_rehaul_v2') {
        this.regimenRehaulV2 = true;
      }
      if (exp.key === 'instructions_language_change') {
        this.instructionsLanguageChangeExp = true;
      }
      if (exp.key === 'regimen_expiry_banner') {
        this.regimenExpiryExperiment = exp;
      }
      if (exp.key === 'chat_skip_last_questions') {
        this.chatSkipLastQuestionsExperiment = exp;
      }
      if (exp.key === 'diet_banner_v2') {
        this.dietBannerV2 = true;
      }
      if (exp.key === 'ret_homepage_revamp') {
        this.dietBannerRevamp = true;
      }
    });
  }

  activateSubscription(): void {
    this.subscriptions.push(this.route.queryParams.subscribe(async (params: any): Promise<any> => {
      if (params.resetSelection) {
        this.regimen = this.regimens.find((each: any): boolean => (each.class !== params.class));
      }
      if (params.showTimer) {
        this.regimen = {} as AllocatedRegimen;
        this.router.navigate([], {queryParams: {showTimer: null}, queryParamsHandling: 'merge', replaceUrl: true});
      }
      this.updateAddOnInstructionsFlags();
      if (params.tab !== 'regimen' || (this.regimen && params.class && params.class === this.regimen.class)) {
        this.updateStatusBarColor();
        return;
      }

      if (params.class === 'OTHERS') {
        // If add-on instructions don't exist, redirect to home
        if (this.addOnInstructions()?.length === 0) {
          await this.router.navigate(['/user'], { queryParams: { tab: 'home', init: 'true' }, replaceUrl: true });
          return;
        }

        // If add-on instructions exist, show the OTHERS UI (triggered by renderAddOnInstructionsUI navigation)
        // Just update flags and return - the UI is controlled by shouldShowAddOnInstructionUI
        this.updateAddOnInstructionsFlags();
        this.updateStatusBarColor();
        return;
      }

      this.localStorageService.setValue('regimenUpdateVisited', true);
      const regimenClass = params?.class === this.appConfig.Shared.Regimen.Class.BODY
        ? this.appConfig.Shared.Regimen.Class.FACE : params?.class;
      await this.fetchRegimens(regimenClass, params.regimenId);
      if (await this.redirectToMainConcernIfEmpty(regimenClass)) return;
      if (params.instructionPage) this.showInstructionPage();
      this.updateStatusBarColor();
    }));
  }

  private async redirectToMainConcernIfEmpty(regimenClass: string): Promise<boolean> {
    if (
      this.experimentService.isBottomNavConfigActive() &&
      this.showRegimenEmptySection &&
      (regimenClass === this.appConfig.Shared.Regimen.Class.FACE ||
       regimenClass === this.appConfig.Shared.Regimen.Class.HAIR)
    ) {
      await this.router.navigate(['/mainConcern'], {
        queryParams: { class: regimenClass, redirectOnBack: '/home' },
        replaceUrl: true,
      });
      return true;
    }
    return false;
  }

  private updateStatusBarColor(): void {
    if (!this.loading && this.regimen.class && !this.regimen.objectId && !this.consultationSession && !this.shouldShowAddOnInstructionUI()) {
      this.appBridge.setStatusBarColor('cureBlack');
    } else if (this.shouldShowConsultationPage) {
      this.appBridge.setStatusBarColor('cureBlack');
    } else if (this.regimen && this.regimen.showPriceDetail && !this.shouldShowAddOnInstructionUI()) {
      this.appBridge.setStatusBarColor('cureBlack');
    } else if (this.shouldShowInstructionsHeader && !this.loading) {
      this.appBridge.setStatusBarColor('cureBlack');
    } else {
      this.appBridge.setStatusBarColor('cure/light');
    }
  }

  private async validateAndRedirectForOpenCallRequest(userRegimens: Array<AllocatedRegimen>, requestedClass?: string): Promise<boolean> {
    const openCallRequest = this.route.snapshot.queryParams['openCallRequest'];

    if (openCallRequest && requestedClass) {
      const hasAllocatedRegimen = userRegimens.some((regimen: AllocatedRegimen): boolean =>
        regimen.class === requestedClass && !regimen.orderPlaced
      );

      if (!hasAllocatedRegimen) {
        await this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
        return true;
      }
    }
    return false;
  }

  private async fetchRegimens(defaultActiveClass?: any, regimenId?: any): Promise<any> {
    if (!this.user) return;
    this.loading = true;
    this.conn.allowRegimenForceFetch = true;
    try {
      const include: ParseKeys<Table.Regimen>[] = [
        'morning.product' as 'morning',
        'night.product' as 'night',
        'regimenTag',
        'variants.products' as 'variants',
        'variants.morning.product' as 'variants',
        'variants.night.product' as 'variants',
        'variants.morning.product.heroIngredients' as 'variants',
        'variants.night.product.heroIngredients' as 'variants',
        'morning.product.heroIngredients',
        'night.product.heroIngredients',
        'variants.morning.product.productHighlights' as 'variants',
        'variants.night.product.productHighlights' as 'variants',
        'morning.product.productHighlights',
        'night.product.productHighlights',
      ] as ParseKeys<Table.Regimen>[];

      // Preload add-on instructions in parallel with regimens when loading HAIR/FACE
      const fetchRegimensPromise = this.conn.fetchRegimens(regimenId, true, false, include);
      const fetchAddOnInstructionsPromise = this.user?.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED
        && this.addOnInstructions()?.length === 0 // Only fetch if not already loaded
        ? this.fetchAndUpdateAddOnInstructionsFlags()
        : Promise.resolve();

      // Fetch both in parallel
      const [fetchedUserRegimens] = await Promise.all([fetchRegimensPromise, fetchAddOnInstructionsPromise]);
      this.userRegimens.set(fetchedUserRegimens);

      const requestedClass = this.route.snapshot.queryParams['class'];

      if (await this.validateAndRedirectForOpenCallRequest(this.userRegimens(), requestedClass)) {
        return;
      }
      const isBodyRegimen = (regimen: AllocatedRegimen): boolean => regimen.class === this.appConfig.Shared.Regimen.Class.BODY && regimen.active;
      const foundBodyRegimen = this.userRegimens().find(isBodyRegimen);
      this.hasBodyRegimen = !!foundBodyRegimen;
      this.setTabData(this.hasBodyRegimen, this.addOnInstructions()?.length > 0);
      this.processRegimenDetails();
      await this.chooseActiveRegimenClass(defaultActiveClass);
    } catch (error) {
      this.eventLoggerService.trackEvent('ERROR_fetchRegimens', { error: error, from: 'user-view-regimen' });
    } finally {
      this.loading = false;
      this.loadingByChild = false;
    }
  }

  setTabData(hasBodyRegimen: boolean, hasAddOnInstruction: boolean): void {
    let tabCount = 2;
    if (hasBodyRegimen && hasAddOnInstruction) {
      tabCount = 4;
    } else if (hasBodyRegimen || hasAddOnInstruction) {
      tabCount = 3;
    }
    const selectedClass = this.regimenClasses.indexOf(this.regimen.class)
    this.tabData = {
      count: tabCount,
      types: [
        'REGIMEN',  // Face - always present
        'REGIMEN',  // Hair - always present
        ...(hasBodyRegimen ? ['BODY'] : []),        // Body - conditional
        ...(hasAddOnInstruction ? ['OTHERS'] : [])  // Others/Add-ons - conditional
      ],
      selectedTab: this.route.snapshot.queryParams.class === 'OTHERS' && !this.hasBodyRegimen ? 2 : selectedClass,
    };

    // Fire a one-time impression event when the Others tab option becomes visible
    // Fire a one-time impression event when the Others tab option becomes visible
    if (hasAddOnInstruction && !this.hasSentOthersTabImpression) {
      this.hasSentOthersTabImpression = true;
      this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
        name: 'others_tab_visible',
      }));
    }
  }

  setIsRegimenNewPropositionUI(): void {
    try {
      this.experiments.forEach((exp: any): void => {
        if (exp.key === 'regimen_new_proposition_ui') {
          this.isRegimenNewPropositionUI = this.regimen.class === this.appConfig.Shared.Regimen.Class.FACE;
        }
      });
    } catch (err) {
      this.isRegimenNewPropositionUI = false;
    }
  }

  /** Chooses which class of regimen to be shown
   * 1. shows unbought regimen (i.e not orderPlaced & not active)
   * 2. else , shows recent active regimen tab
   * 3. else , shows 1st available regimen tab
   * 4. else , shows user's mainconcern class tab  or default class (FACE)
   * @param defaultActiveClass - default class to be selected.
   */
  private async chooseActiveRegimenClass(defaultActiveClass?: string): Promise<any> {
    let activeClass;
    let defaultReg;
    if (this.isFromHowToUse) {
      defaultReg = this.userRegimens().find((each: AllocatedRegimen): boolean => !!each.active);
    }
    if (!defaultReg) defaultReg = this.userRegimens().find((each: AllocatedRegimen): boolean => !each.active && !each.orderPlaced);
    if (!defaultReg) defaultReg = this.userRegimens().find((each: AllocatedRegimen): boolean => !!each.active);
    if (defaultReg && defaultReg.class) activeClass = defaultReg.class;
    else {
      activeClass = this.userRegimens()?.length
        ? this.userRegimens()[0].class
        : (this.user?.get('PrivateMainConcernClass') || this.appConfig.Shared.Regimen.Class.FACE);
    }
    await this.selectRegimenOfClass(defaultActiveClass || activeClass);
  }

  /** Chooses the regimen of selected class.
   * Updates the flag of regimen based of state of regimen it is, which helps to show the specific component.
   * Routes to same regimen tab with class in queryParam in order to save it in browser history. which helps to show that specific
   * class again when visited back.
   * @param regimenClass - class of regimen to be selected.
   */
  private async selectRegimenOfClass(regimenClass: string): Promise<any> {
    if (regimenClass === 'OTHERS') {
      // OTHERS is handled by tabChange -> renderAddOnInstructionsUI, not here
      return;
    };
    if (!this.isTabActive || regimenClass === 'OTHERS') return;
    this.tabSwitching = true;
    if (this.regimenModuleContainer) {
      this.regimenModuleContainer.nativeElement.scrollTop = 0;
    }
    this.regimen = this.regimens.find((regimen: AllocatedRegimen): boolean => (regimen.class === regimenClass));
    this.setIsRegimenNewPropositionUI();

    this.tabData.selectedTab = this.regimenClasses.findIndex((each: any): boolean => each === this.regimen.class);
    try {
      await this.updateRegimenDisplayFlags(this.regimen);
    } catch (error) {
      this.eventLoggerService.trackEvent('ERROR_updateRegimenDisplayFlags', { error: error, regimenClass: regimenClass, from: 'user-view-regimen' });
    } finally {
      this.tabSwitching = false;
    }

    // Regimen route refactor: navigate to shell if this view is migrated
    const viewName: RegimenViewState | null = this.getViewNameForCurrentState();
    if (viewName && this.experimentService.regimenRouteVariant?.[viewName] === true) {
      const routePath = VIEW_TO_ROUTE_MAP[viewName];
      if (routePath) {
        await this.router.navigate([REGIMEN_ROUTE_PATHS.ROOT, routePath], {
          queryParams: { [REGIMEN_QUERY_PARAMS.CLASS]: regimenClass },
          replaceUrl: true,
        });
        return;
      }
    }

    if (this.isInternalUser) return;
    setTimeout((): void => {
      this.router.navigate(
        ['/user'],
        {
          queryParams: {
            tab: 'regimen',
            class: this.regimen.class,
            supportTicketId: this.route.snapshot.queryParams.supportTicketId,
            username: this.route.snapshot.queryParams.username,
          },
          replaceUrl: true,
        });
      this.updateStatusBarColor();
    }, 0);
    await this.fetchUnpaidUserDoctorCalls();
  }

  private getViewNameForCurrentState(): RegimenViewState | null {
    if (!this.regimen.objectId && this.consultationSession) return REGIMEN_VIEW_STATES.CHAT;
    if (!this.regimen.objectId) return REGIMEN_VIEW_STATES.EMPTY;
    if (this.regimen.regimenPreparation) return REGIMEN_VIEW_STATES.PREPARATION;
    if (this.regimen.showProductSummary) return REGIMEN_VIEW_STATES.INSTRUCTION;
    if (this.regimen.showConsultationView) return REGIMEN_VIEW_STATES.CONSULTATION;
    if (this.regimen.showPriceDetail) return REGIMEN_VIEW_STATES.UNPAID;
    return null;
  }

  /**
   * Process regimen's total mrp, sp, discount and normalizes few more information for easy access in child components.
   * And updates the normalized regimens in 'this.regimen' from 'this.userRegimens'. 'this.regimens' will be used across after this process.
   */
  private processRegimenDetails(): void {
    this.userRegimens().forEach((regimen_: AllocatedRegimen): void => {
      const regimen = regimen_;
      this.updateUserRegimenInRegimenList(regimen);
      if (!regimen.objectId) return;
      regimen.totalMRP = Math.round(regimen.fixedPriceMRP);
      regimen.totalSP = regimen?.fixedPrice;
      regimen.discount = Math.floor(((regimen.totalMRP - regimen.totalSP) * 100) / regimen.totalMRP);

      const { morning, night }: any = regimen;
      [].concat(morning).concat(night).forEach((item_: any): void => {
        const item = item_;
        if (item.instruction && typeof item.instruction === 'string') {
          item.instruction = item.instruction.split('.').filter((ins: any): boolean => ins.trim().length > 0);
        } else if (!item.instruction) {
          item.instruction = [item.product?.purposeDescription, 'Purchase regimen to see your instructions.'];
          if (this.showDescriptionExperiment) item.instruction = [item.product.description, 'Purchase regimen to see your instructions.'];
        }
      });
    });
  }

  private updateUserRegimenInRegimenList(userRegimen: AllocatedRegimen): void {
    this.regimens = this.regimens.map((emptyRegimen: AllocatedRegimen): AllocatedRegimen => {
      // Replaces processed regimen in the regimens template.
      if (emptyRegimen.class === userRegimen.class) return userRegimen;
      return emptyRegimen;
    });
  }

  /**
   * Sets the flag in regimen based on regimen state
   * 1. regimenPreparation - if regimen is in preparation time.
   * 2. showProductSummary - if queryParam 'instructions' is set or once regimen become active
   * 4. showPriceDetail - default, if regimen is unbought.
   * @param regimen_ - regimen in which flags are to be set.
   */
  async updateRegimenDisplayFlags(regimen_: AllocatedRegimen): Promise<void> {
    const regimen = regimen_;
    if (!regimen.objectId) {
      const session = await this.conn.findConsultationSession(this.regimen?.class);
      if (session?.get('PrivateMainConcern')) {
        this.consultationSession = session;
        try {
          await this.conn.switchUserToState(
            `v4_${this.consultationSession?.get('PrivateMainConcernClass').toLowerCase()}:`,
            { consultationSessionId: this.consultationSession.id });
        } catch (e) {
          this.eventLoggerService.trackEvent('ERROR_switchUserToState', { error: e, consultationSessionId: this.consultationSession.id, from:'user-view-regimen' });
        }
        return;
      }
      this.consultationSession = undefined;
      return;
    }

    this.consultationSession = undefined;
    this.resetRegimenDisplayFlags(regimen);

    const hasInstructionSet = regimen.morning.every((each: any): boolean => each.instructionSet !== undefined)
      && regimen.night.every((each: any): boolean => each.instructionSet !== undefined);

    if (this.commonUtil.isRegimenBeingPrepared(regimen)) {
      regimen.regimenPreparation = true;
    }

    if (this.route.snapshot.queryParams.instructions) {
      regimen.showProductSummary = true;
      return;
    }
    if (regimen.regimenPreparation) return;
    if (![...regimen.morning, ...regimen.night].length) regimen.showConsultationView = true;
    else if (regimen.showSummaryView && regimen.active && hasInstructionSet) regimen.showProductSummary = true;
    else regimen.showPriceDetail = true;
  }

  private resetRegimenDisplayFlags(regimen_: any): void {
    const regimen = regimen_;
    regimen.showConsultationView = false;
    regimen.showProductSummary = false;
    regimen.showPriceDetail = false;
    regimen.regimenPreparation = false;
    if (this.regimenModuleContainer) {
      this.regimenModuleContainer.nativeElement.scrollTop = 0;
    }
  }

  private showInstructionPage(): void {
    this.resetRegimenDisplayFlags(this.regimen);
    this.regimen.showProductSummary = true;
  }

  resetFlagAndShowRegimen(): void {
    this.redirectAfterRegimenPreparation = true;
    this.showRegimen.emit(true);
    this.updateRegimenDisplayFlags(this.regimen);
  }

  async readMoreAboutProducts(): Promise<void> {
    this.eventLoggerService.trackEventForClass(FirebaseAndBranchEvent.REGIMEN_VIEW_READ_ABOUT_PRODUCTS, this.regimen.class, { username: this.user?.get('username') });
    if (this.route.snapshot.queryParams.class === 'OTHERS') {
      await this.router.navigate(['/products'], { queryParams: { overrideRegimen: true } });
      return;
    }
    await this.router.navigate(['/user/regimen/products'],
      { queryParams: { class: this.regimen.class, username: this.user?.get('username'), regimenId: this.regimen.regimenId } });
  }

  async showPriceDetails(): Promise<void> {
    this.eventLoggerService.trackInParse('KNOW_MORE', ApiClientConstant.Event.Type.ORDER);
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'instruction-full-details' }));
    this.resetRegimenDisplayFlags(this.regimen);
    if (this.regimen?.active || this.route.snapshot.queryParams.class === 'OTHERS') {
      await this.readMoreAboutProducts();
      return;
    }
    if (this.experimentService.regimenRouteVariant?.[REGIMEN_VIEW_STATES.UNPAID]) {
      await this.router.navigate([REGIMEN_ROUTE_PATHS.ROOT, REGIMEN_ROUTE_PATHS.UNPAID], {
        queryParams: { [REGIMEN_QUERY_PARAMS.CLASS]: this.regimen.class },
        replaceUrl: true,
      });
      return;
    }
    this.regimen.showPriceDetail = true;
    if (this.regimenModuleContainer) {
      this.regimenModuleContainer.nativeElement.scrollTop = 0;
    }
  }

  showSummaryView(forceInstruction?: boolean): any {
    if (forceInstruction || this.regimen.orderPlaced) {
      this.resetRegimenDisplayFlags(this.regimen);
      this.regimen.showProductSummary = true;
      if (this.regimenModuleContainer) {
        this.regimenModuleContainer.nativeElement.scrollTop = 0;
      }
      return;
    }

    this.router.navigate(['/user/regimen/products'],
      { queryParams: { class: this.regimen.class, username: this.user.get('username'), regimenId: this.regimen.regimenId } });
  }

  async buyRegimen(): Promise<void> {
    await this.regimenService.buyRegimen(this.regimen, this.user);
  }

  async reorder(): Promise<any> {
    if (this.isInternalUser) return;
    const morningProducts = this.regimen.morning.map((morning: any): string => morning.product.objectId);
    const nightProducts = this.regimen.night.map((morning: any): string => morning.product.objectId);
    const ids = Array.from(new Set([...morningProducts, ...nightProducts]));
    this.conn.navigateToURL(`/cart?products=${ids.join(',')}&type=REGIMEN`);
  }

  /**
   * Triggers tree for the regimen and routes to chat tab to start answering the BOT questions. if the consultation session exists.
   * Else route to main concern page of specific class to start the consultation.
   */
  async triggerTreeForRegimen(): Promise<void> {
    const session = await this.conn.findConsultationSession(this.regimen?.class);
    if (session && session?.get('PrivateMainConcern')) {
      try {
        const concerns = (session?.get('concernList') || []).length ? session?.get('concernList') : [session?.get('PrivateMainConcern')];
        await this.conn.saveUserMainConcern(concerns);
        await this.router.navigate(['/user'],
          { queryParams: { tab: 'chat', username: this.user?.get('username') } });
        this.updateStatusBarColor();
      } catch (err) {
        this.broadcast.broadcast('NOTIFY', { message: err.toString() });
      }
    } else {
      await this.router.navigate(['/mainConcern'], { queryParams: { class: this.regimen.class, redirectOnBack: '/home' } });
    }
  }

  async openRegimenOrderStatus(): Promise<void> {
    await this.regimenService.openRegimenOrderStatus(this.user, this.regimen);
  }

  swipeCallback(event: any): void {
    if (!this.regimen.class) return;
    let curIndex = this.regimenClasses.findIndex((each: any): boolean => each === this.regimen.class);
    if (curIndex < 0) curIndex = 0;
    if (event === 'LEFT' && curIndex < this.regimenClasses.length - 1) {
      curIndex += 1;
    } else if (event === 'RIGHT' && curIndex > 0) {
      curIndex -= 1;
    }
    this.selectRegimenOfClass(this.regimenClasses[curIndex]);
  }

  tabChange(event: {index: number, type: string}): void {
    if (event.type === 'OTHERS') {
      this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
        name: 'instructions_others',
      }));
      this.updateAddOnInstructionsFlags();
      this.renderAddOnInstructionsUI();
      return;
    }
    this.shouldShowAddOnInstructionUI.set(false);

    const targetClass = this.regimenClasses[event.index];
    if (!this.regimen?.class) {
      this.router.navigate(['/user'], {
        queryParams: { tab: 'regimen', class: targetClass },
        replaceUrl: true,
      });
      return;
    }
    this.selectRegimenOfClass(targetClass);
  }

  renderAddOnInstructionsUI(): void {
    if (!this.isTabActive) return;
    this.consultationSession = undefined;
    if (this.regimenModuleContainer) {
      this.regimenModuleContainer.nativeElement.scrollTop = 0;
    }
    setTimeout((): void => {
      this.router.navigate(
        ['/user'],
        {
          queryParams: {
            tab: 'regimen',
            class: 'OTHERS',

          },
          replaceUrl: true,
        });
      this.updateStatusBarColor();
    }, 0);
  }

  /**
   * If back pressed from regimen summary view, it shows the price detail view and cancels the browser back navigation.
   * Else it tells the browser to handle it by returning false (i.e means not handled locally)
   */
  async handleBackPress(): Promise<any> {
    if (this.regimen && this.regimen.showProductSummary && !this.regimen.active) {
      await this.showPriceDetails();
      return true;
    }
    this.router.navigate(['/user'], { queryParams: { tab: 'home', init: 'true' }, replaceUrl: true });
    return false;
  }

  private async isRehaulBottomsheetShown(): Promise<boolean> {
    // Shared logic lives in UnpaidPendingCallService so the unpaid route component reuses it.
    // A created call is refetched via the callCreated$ subscription set up in ngOnInit.
    return this.pendingCallService.showDoctorCallNudgeOnBack({
      regimen: this.regimen,
      user: this.user,
      nudgeDoctorCallOnBack: this.nudgeDoctorCallOnBack,
      unpaidUserDoctorCallExpVariants: this.unpaidUserDoctorCallExpVariants,
      unpaidUserDoctorCalls: this.unpaidUserDoctorCalls,
    });
  }

  async fetchUnpaidUserDoctorCalls(): Promise<void> {
    this.unpaidUserDoctorCalls = await this.pendingCallService.getFilteredPendingCalls(this.regimen.objectId);
  }

  async back(): Promise<void> {
    const callNudgeShown: boolean = await this.isRehaulBottomsheetShown();
    if (callNudgeShown) return;

    const supportTicketId = this.route.snapshot.queryParams;

    if (this.regimen?.showProductSummary && !supportTicketId) {
      this.handleBackPress();
      return;
    }

    if (this.shouldShowConsultationPage && !this.consultationSession?.get('PrivateTreeCompleted')) {
      const sheetRef = this.consultationSessionService.validateAndOpenSkipConsultationQuestionsSheet(
        this.userFirstName,
        this.chatSkipLastQuestionsExperiment,
        this.consultationSession,
      );
      if (sheetRef) {
        return;
      }
    }

    if (supportTicketId) {
      this.router.navigate(['/support/ticket'], { replaceUrl: true });
    }

    this.router.navigate(['/user'], { queryParams: { tab: 'home', init: 'true' }, replaceUrl: true });
  }

  updatePrice(cartObject: any): void {
    // list of selected products -> needed for updateCart API
    // selectedProducts original price and selling price
    const cartState: any = cartObject?.cartState;
    // This property is used to differentiate b/w targetted regimens
    // Cart updation should happen only for face regimen
    // and inside face regimen, we should update the cart only for user interaction, not for the backend flow.
    // Because then price mismatch will happen
    const isCartUpdated: any = cartObject?.isCartUpdated;
    this.cartState = { ...cartState };
    const totalCartPrice = this.regimen.totalMRP + cartState.totalPrice;
    const totalCartDiscount = this.regimen.totalSP + cartState.discountedPrice;
    this.cartDiscount = Math.floor(((totalCartPrice - totalCartDiscount) / totalCartPrice) * 100);
    this.isCartUpdated = isCartUpdated;
  }

  async updateRegimen(regimen_: AllocatedRegimen): Promise<void> {
    // update the price
    const regimen = regimen_;
    regimen.totalMRP = Math.round(regimen.fixedPriceMRP);
    regimen.totalSP = regimen?.fixedPrice;
    regimen.discount = Math.floor(((regimen.totalMRP - regimen.totalSP) * 100) / regimen.totalMRP);

    // Update the regimen reference to trigger change detection
    this.regimen = regimen;

    // Update the regimen in the regimens array so it has the correct data when switching back
    this.updateUserRegimenInRegimenList(regimen);

    if (this.regimenModuleContainer && !this.regimenRehaulV2) {
      this.regimenModuleContainer.nativeElement.scrollTop = 0;
    }
  }

  /** Removes component from currentComponentService, so that no other component handles this components back press. */
  ngOnDestroy(): void {
    this.isTabActive = false;
    this.showRegimen.emit(false);
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
    this.subscriptions = [];
    this.currentComponentService.remove(this);
    if (this.regimenModuleContainer) {
      this.regimenModuleContainer.nativeElement.removeEventListener('scroll', this.onScroll.bind(this));
    }
  }

  parentLoading(loading: boolean): void {
    this.loadingByChild = loading;
  }

  selectedPlan(plan: string): void {
    this.plan = plan;
  }

  get discountSpinnerFillColor(): string {
    switch (this.plan) {
      case 'regular': return '#76539A';
      case 'advanced': return '#D1903E';
      case 'pro': return '#4973B4';
      default: return '#D66736';
    }
  }

  openLanguageChangeDrawer(): void {
    if (this.botReplyInProgress) return;
    const botChats = this.consultationSessionService.getChats().filter((chat: any): boolean => chat.get('Owner') === 'BOT');
    this.latestBotChat = botChats.length > 0 ? botChats[botChats.length - 1] : undefined;
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'chat-language-change-pill' }));
    this.bottomSheet.open(LanguageChangeComponent, {
      data: {
        translateLastBotQuestionData: {
          questionId: this.latestBotChat?.get('questionId'),
          regimenClass: this.consultationSession?.get('PrivateMainConcernClass'),
        },
        translateChatMessages: (!(this.instructionsLanguageChangeExp && this.regimen && this.regimen.showProductSummary)), // no need to translate chat in retention instruction page
      },
    });
  }

  handleLoadingStatus(isLoading: boolean): void {
    this.isChatLoading = isLoading;
  }

  ngAfterViewInit(): void {
    if (this.regimenModuleContainer) {
      this.regimenModuleContainer.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
    }
  }

  public onScroll(): void {
    const SCROLL_THRESHOLD = 3;
    const { scrollTop }: { scrollTop: number } = this.regimenModuleContainer.nativeElement;

    // Check if the scroll difference exceeds the threshold
    if (Math.abs(scrollTop - this.lastScrollTop) > SCROLL_THRESHOLD) {
      this.scrollingDown = scrollTop > this.lastScrollTop;
    }

    this.lastScrollTop = scrollTop;
  }
  botReplyInProcessChange(botReplyState: boolean): void {
    this.botReplyInProgress = botReplyState;
  }

  async onReminderToggleChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    await this.toggleReminders(input.checked);
  }

  async toggleReminders(enabled: boolean): Promise<void> {
    // Store user reference before any async operations
    const currentUser = this.user;
    if (!currentUser) {
      console.error('User is not defined');
      return;
    }

    // Update local state immediately for responsive UI
    this.reminderToggle = enabled;

    try {
      // Save to backend first
      await this.setInstructionReminderStatus(enabled, currentUser);
      this.showReminderContainer = true;

      // Then update UI state
      if (enabled) {
        this.reminderEventSent = true;
        // Close reminder container after delay
        setTimeout((): void => {
          this.closeReminder();
          // show success animation if closed before
          if (!this.reminderToggle) {
            this.reminderEventSent = false;
            this.showReminderContainer = true;
          }
        }, this.REMINDER_CLOSE_DELAY_MS);
      } else {
        this.reminderEventSent = false;
        this.showReminderContainer = true;
      }

      // Log event after successful save
      this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
        name: 'instruction-reminder',
        userId: currentUser.id,
        enabled: enabled
      }));
      this.eventLoggerService.setUserProperty({ is_instruction_reminder_enabled: enabled });
    } catch (error) {
      // Revert local state if save fails
      this.reminderToggle = !enabled;
      console.error('Failed to update reminder status:', error);
    }
  }

  closeReminder(): void {
    this.showReminderContainer = false;
  }

  async setInstructionReminderStatus(hasEnabled: boolean, user: any): Promise<void> {
    if (!user) {
      throw new Error('User is required to set reminder status');
    }

    user.set('hasEnabledReminderForInstructions', hasEnabled);
    await user.save();

    // Update the component's user reference after save
    this.user = user;
  }

  isMorningNightTabsVisibilityChange(isVisible: boolean): void {
    this.isMorningNightTabsVisible = isVisible;
  }

  async fetchAllocatedDoctorForUser(): Promise<void> {
    if (this.user?.get('allocatedDoctor')?.id) {
      this.allocatedDoctor = await this.userAllocatedDoctorService.getAllocatedDoctor();
    }
  }

  async shouldShowRegimenExpiryBanner(): Promise<void> {
    const result = await this.regimenExpiryService.shouldShowRegimenExpiryBanner({
      user: this.user,
      regimenExpiryExperiment: this.regimenExpiryExperiment,
      allocatedDoctor: this.allocatedDoctor,
      defaultDoctorImage: this.defaultDoctorImage
    });
    this.regimenExpiryBannerData = result.bannerData;
    this.showRegimenExpiryBanner = result.showBanner;
    if (result.showProductBottomSheet && !this.allocatedDoctor && !this.userDrImage) {
      await this.fetchAllocatedDoctorForUser();
      this.userDrImage = this.allocatedDoctor?.doctorDisplayImage || this.allocatedDoctor?.DoctorImageThumbnail || this.defaultDoctorImage;
    }
  }

  handleRegimenExpiryBannerClick(): void {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'instructions_regimen_will_expire' }));
    this.regimenExpiryService.handleRegimenExpiryBannerClick(this.regimenExpiryBannerData, this.user, this.userDrImage, RegimenExpiryBottomsheetComponent);
  }

  get isRegimenExpiredForPaidUser(): boolean {
    const isPaidUser = this.user?.get('orderState') === ApiClientConstant.User.OrderState.DELIVERED;
    return this.regimenExpiryService.isRegimenExpiredForPaidUser(isPaidUser, this.regimens);
  }

  get showRegimenEmptySection(): boolean {
    return !this.loading && !this.tabSwitching && this.regimen.class && !this.regimen.objectId && !this.consultationSession && !this.shouldShowAddOnInstructionUI();
  }

  get shouldShowLanguageDropDown(): boolean {
    if (this.loading) return false;
    const isExperimentEnabled = this.instructionsLanguageChangeExp;
    const hasRegimen = this.regimen;
    const shouldShowInstructions = this.regimen?.showProductSummary || this.shouldShowAddOnInstructionUI();

    return isExperimentEnabled && hasRegimen && shouldShowInstructions;
  }

  get shouldShowInstructionsHeader(): boolean {
    if (this.loading) return false;
    const isRegimenInstructions = this.regimen?.showProductSummary;
    const isAddOnInstructions = this.shouldShowAddOnInstructionUI();
    return isRegimenInstructions || isAddOnInstructions;
  }

  get shouldShowGetYourTreatmentHeader(): boolean {
    if (this.loading) return false;
    return !this.regimen?.showProductSummary && !this.shouldShowAddOnInstructionUI();
  }

  get shouldShowRegimenInstruction(): boolean {
    return !this.loading && !this.tabSwitching && this.regimen && this.regimen.showProductSummary && !this.shouldShowAddOnInstructionUI();
  }

  get shouldShowRegimenUnpaid(): boolean {
    return !this.loading && !this.tabSwitching && this.regimen && this.regimen.showPriceDetail && !this.shouldShowAddOnInstructionUI();
  }

  get shouldShowRegimenConsultationView(): boolean {
    return !this.loading && !this.tabSwitching && this.regimen && this.regimen.showConsultationView && !this.shouldShowAddOnInstructionUI();
  }

  get shouldShowRegimenPreparationUI(): boolean {
    return !this.tabSwitching && this.regimen && this.regimen?.regimenPreparation && !this.shouldShowAddOnInstructionUI()
  }

  get shouldShowConsultationPage(): boolean {
    return !this.loading && !this.tabSwitching && this.consultationSession && !this.shouldShowAddOnInstructionUI();
  }

  get shouldShowRegimenFooter(): boolean {
    return !!this.regimen.class && !this.loading && !this.tabSwitching && !this.shouldShowAddOnInstructionUI();
  }

  get shouldShowChatProgressBar(): boolean {
    return !!this.consultationSession && this.chatProgressType === Table.ConsultationSession.className && !this.isChatLoading && this.isNotIOSDevice && !this.tabSwitching && !this.loading;
  }
}
